import User from "./user";
import SortOrders from "./sort-orders";
import Parameter from "./parameter";
import Field from "./field";
import Entity from "./entity";
import Api from "./api";
import { sortByOrder } from "../utils";
import IConfigFormulaRenewal from "../interface/field-cfg-formula-renewal";
import IConfigFormula from "../interface/field-cfg-formula";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocument from "../interface/document";
import IDataExportTemplate from "../interface/data-export-template";
import IDataExportMapItem from "../interface/data-export-map-item";
import { IDataExportFieldOptions } from "../interface/data-export-field-options";
import ParamStorage from "../enum/param-storage";
import FormulaType from "../enum/formula-type";
import { DataExportTypes } from "../enum/field-types";
import FieldType from "../enum/field-type";
import DataExportSourceType from "../enum/data-export-source-type";
import ParameterStorage from "../class/parameter-storage";

class DataExportSingleton {

    private readonly Store = ParamStorage.DataExport;

    public async run(tpl: IDataExportTemplate, docs: IDocument[]): Promise<void> {
        if (User.isDesigner()) {
            if (tpl.id) {
                await this.update(tpl);
            } else {
                await this.create(tpl);
            }
        }
        return await this.export(tpl, docs);
    }

    public getAll(entityId: number | null = null): IDataExportTemplate[] {
        const all: IDataExportTemplate[] = this.getStore().getAll();
        if (entityId) {
            const order: number[] = SortOrders.get(this.Store, entityId);
            const result: IDataExportTemplate[] = all.filter((tpl: IDataExportTemplate) => tpl.entityId === entityId);
            return sortByOrder(result, 'id', order);
        }
        return all;
    }

    public get(id: number): IDataExportTemplate | null {
        return this.getStore().get(id);
    }

    public async delete(id: number): Promise<number> {
        return this.getStore().delete(id);
    }

    public getFieldsOptions(srcId: number, destId: number): IDataExportFieldOptions[] {
        const result: IDataExportFieldOptions[] = [];
        const srcEntity: IEntity | null = Entity.get(srcId);
        const destEntity: IEntity | null = Entity.get(destId);
        if (srcEntity && destEntity) {
            Entity.getFields(destEntity.id).forEach((field: IField) => {
                if (DataExportTypes.includes(field.uiType)) {
                    const options: IField[] = this.getFieldOptions(field, srcId);
                    result.push({
                        linkToSource: field.uiType === FieldType.linked && field.foreignerEcn === srcId,
                        options,
                        field
                    });
                }
            });
        }
        return result;
    }

    public async setOrder(entityId: number | null = null, order: number[]): Promise<IDataExportTemplate[]> {
        await SortOrders.set({ param: this.Store, entityId, order });
        return this.getAll(entityId);
    }

    /* Private Public Methods */

    public async create(tpl: IDataExportTemplate): Promise<IDataExportTemplate> {
        return this.getStore().create(tpl);
    }

    public async update(tpl: IDataExportTemplate): Promise<IDataExportTemplate> {
        return this.getStore().update(tpl);
    }

    /* Private Methods */

    private async export(tpl: IDataExportTemplate, source: IDocument[]): Promise<void> {
        if (source.length) {
            const result: IDocument[] = [];
            source.forEach((srcDoc: IDocument) => {
                const doc: IDocument = {};
                tpl.mapping.forEach((map: IDataExportMapItem) => this.setDocumentValue(map, doc, srcDoc));
                result.push(doc);
            });
            if (result.length) {
                await Api.insertDocuments(tpl.destinationId, result);
            }
        } 
    }

    private setDocumentValue(map: IDataExportMapItem, doc: IDocument, srcDoc: IDocument): void {
        const field: IField | null = Field.get(map.fieldId);
        if (field) {
            if (map.type === DataExportSourceType.ExternalField) {
                const src: IField | null = Field.get(map.sourceFieldId as number);
                if (src) {
                    if (src.uiType === field.uiType) {
                        if (field.uiType === FieldType.linked) {
                            doc[`c${field.linkedRelation[0]}`] = srcDoc[`c${src.linkedRelation[0]}`];
                        } else {
                            doc[`c${field.id}`] = srcDoc[`c${src.id}`];
                        }
                    } else if (src.uiType === FieldType.formula) {
                        const config: IConfigFormula = src.uiConfig as IConfigFormula;
                        if (config.type === FormulaType.renewal && field.uiType === FieldType.datetime) {
                            const formula: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
                            if (!formula.showPeriod && srcDoc['d' + src.id]) {
                                doc[`c${field.id}`] = srcDoc['d' + src.id];
                            }
                        } else if (config.type === FormulaType.number && field.uiType === FieldType.number) {
                            if (typeof srcDoc['d' + src.id] === 'number') {
                                doc[`c${field.id}`] = srcDoc[`d${src.id}`];
                            }
                        }
                    }
                }
            } else if (map.type === DataExportSourceType.Custom) {
                if (field.uiType === FieldType.linked) {
                    if (map.customValue instanceof Array) {
                        doc[`c${field.linkedRelation[0]}`] = map.customValue;
                    }
                } else {
                    doc[`c${field.id}`] = map.customValue;
                }
            } else if (map.type === DataExportSourceType.LinkToSource) {
                doc[`c${field.linkedRelation[0]}`] = [srcDoc.id as number];
            }
        }
    }

    private getFieldOptions(field: IField, srcEntityId: number): IField[] {
        return Entity.getFields(srcEntityId).filter((source: IField) => {
            if (field.uiType === FieldType.linked && source.uiType === FieldType.linked) {
                return source.foreignerEcn === field.foreignerEcn;
            } else if (source.uiType === FieldType.formula) {
                const config: IConfigFormula = source.uiConfig as IConfigFormula;
                if (config.type === FormulaType.renewal && field.uiType === FieldType.datetime) {
                    return !(config.formula as IConfigFormulaRenewal).showPeriod;
                } else if (config.type === FormulaType.number && field.uiType === FieldType.number) {
                    return true;
                }
            }
            return source.uiType === field.uiType;
        });
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IDataExportTemplate> {
        return Parameter.get(this.Store);
    }

}

const DataExport = new DataExportSingleton();

export default DataExport;